import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { ReactNode, useEffect, useMemo } from 'react';
import { MeterTaskStatusChip } from 'src/components/Chips/MeterTaskStatusChip';
import { useAppDispatch } from 'src/hooks/hooks';
import { baseAPI } from 'src/services/switch-rest-api/baseAPI.service';
import {
  producedTags,
  scopeKey,
  TaskWatch,
  TaskWatchEvent,
  useWatchMeterTasksQuery,
  WatchedTask,
  watchesOf,
} from 'src/services/switch-rest-api/meter-task.service';
import EnhancedReactJson from 'switch-fe-shared/components/ReactJSON/ReactJSON';
import { capitalize } from 'switch-fe-shared/utils/formatting';
import { MeterTaskInput } from 'switch-shared/modules/crud/meter-task/meter-task-input';
import {
  finishedTaskStatuses,
  MeterTask,
  MeterTaskStatus,
} from 'switch-shared/modules/crud/meter-task/meter-task.entity';
import { Serialized } from 'switch-shared/types/serialised';

/** A time of day, with its date when that is not today. */
const time = (at: string | Date) =>
  dayjs(at).isSame(dayjs(), 'day')
    ? dayjs(at).format('HH:mm:ss')
    : dayjs(at).format('DD MMM HH:mm:ss');

/** The task's status and backoff as its events tell them: running from a claim, pending again after a failed attempt, failed after a final one. */
interface TaskState {
  status?: MeterTaskStatus;
  backedOffUntil?: string;
  backedOffCount: number;
}

function stateOf(events: TaskWatchEvent[]): TaskState {
  let status: MeterTaskStatus | undefined;
  let backedOffUntil: string | undefined;
  let backedOffCount = 0;
  for (const event of events) {
    if (event.type === 'status') {
      status = event.status;
      backedOffUntil = event.backedOffUntil ?? undefined;
      backedOffCount = event.backedOffCount;
    }
    if (event.type === 'claimed' || event.type === 'started')
      status = 'claimed';
    if (event.type === 'failed') status = event.final ? 'failed' : 'pending';
    if (event.type === 'completed') status = 'completed';
  }
  return { status, backedOffUntil, backedOffCount };
}

/** What the task acts on, e.g. a parameter or a profile's OBIS, when it names one. */
const subjectOf = (input: MeterTaskInput) =>
  'parameter' in input ? input.parameter : 'obis' in input ? input.obis : '';

/** One line of a task's history, with the JSON an event carried beyond its label. */
interface HistoryLine {
  key: string;
  at: string | Date;
  label: string;
  detail?: object;
}

/**
 * The task's history as its events tell it, one line per event that adds to
 * it. The opening task snapshot, the lifecycle events whose log entries
 * arrive with the task's stored logs, a status that only repeats the status
 * chip and a broken stream add nothing.
 */
function historyOf(events: TaskWatchEvent[]): HistoryLine[] {
  const lines: HistoryLine[] = [];
  let previous: MeterTaskStatus | undefined;
  events.forEach((event, i) => {
    const key = `event-${i}`;
    switch (event.type) {
      case 'log':
        if (event.message)
          lines.push({ key, at: event.at, label: event.message });
        break;
      case 'status': {
        const unfinished =
          event.status === 'pending' || event.status === 'claimed';
        const label = [
          previous && previous !== event.status && capitalize(event.status),
          unfinished &&
            event.backedOffUntil &&
            new Date(event.backedOffUntil) > new Date(event.at) &&
            `Backed off until ${time(event.backedOffUntil)}`,
          unfinished && !event.connected && 'Gateway offline',
        ]
          .filter(Boolean)
          .join(', ');
        if (label) lines.push({ key, at: event.at, label });
        previous = event.status;
        break;
      }
      case 'result':
        lines.push({
          key,
          at: event.at,
          label: 'Result',
          detail: { payload: event.payload },
        });
        break;
      case 'timeout':
        lines.push({
          key,
          at: event.at,
          label:
            event.phase === 'claim'
              ? 'Timed out waiting for the gateway'
              : 'Timed out waiting for the meter',
        });
        break;
      default:
        break;
    }
  });
  return lines;
}

/** The task's fields worth inspecting, without its logs. */
const detailsOf = (task: Serialized<MeterTask>) => ({
  reference: task.reference,
  priority: task.priority,
  createdAt: task.createdAt,
  input: task.input,
});

/**
 * One task on its watch's shared stream: the stored task the stream opened
 * with, the task's events so far, the state they amount to and whether the
 * stream is still open.
 */
const useWatchedTask = (watch: TaskWatch, taskId: string) => {
  const { data } = useWatchMeterTasksQuery(watch);
  return useMemo(() => {
    const events = (data?.chunks ?? []).filter(
      (e) => !('taskId' in e) || e.taskId === taskId
    );
    const opening = events.find((e) => e.type === 'task');
    return {
      events,
      task: opening?.type === 'task' ? opening.task : undefined,
      state: stateOf(events),
      streaming: data?.isStreaming ?? true,
    };
  }, [data, taskId]);
};

/** The task's status chip, with its backoff while it is pending. */
const StateChip = ({
  state: { status, backedOffUntil, backedOffCount },
  size,
}: {
  state: TaskState;
  size?: 'small';
}) =>
  status ? (
    <MeterTaskStatusChip
      type={status}
      size={size}
      backedOffCount={status === 'pending' ? backedOffCount : 0}
      backedOffUntil={status === 'pending' ? backedOffUntil : undefined}
    />
  ) : null;

/** A collapsed section that shows JSON when expanded. */
const JsonSection = ({ title, src }: { title: ReactNode; src: object }) => (
  <Accordion
    disableGutters
    elevation={0}
    sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon fontSize='small' />}
      sx={{
        px: 0,
        minHeight: 0,
        '& .MuiAccordionSummary-content': { my: 0.5 },
      }}
    >
      {title}
    </AccordionSummary>
    <AccordionDetails sx={{ px: 0, pt: 0 }}>
      <EnhancedReactJson src={src} />
    </AccordionDetails>
  </Accordion>
);

/** One timestamped line of the task's history. */
const Line = ({ at, label }: { at: string | Date; label: string }) => (
  <Stack direction='row' gap={1.5}>
    <Typography
      variant='body2'
      color='text.secondary'
      sx={{ width: 120, flexShrink: 0 }}
    >
      {time(at)}
    </Typography>
    <Typography variant='body2' color='text.primary'>
      {label}
    </Typography>
  </Stack>
);

/**
 * A watched task in the list, keeping its watch's stream open while it is
 * listed and refetching what the task wrote once it completes.
 */
const TaskRow = ({
  watched,
  watch,
  selected,
  onSelect,
  onRemove,
}: {
  watched: WatchedTask;
  watch: TaskWatch;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) => {
  const { task, state } = useWatchedTask(watch, watched.taskId);

  const dispatch = useAppDispatch();
  const completed = state.status === 'completed';
  const taskType = task?.input.task;
  useEffect(() => {
    if (!completed || !taskType) return;
    const tags = producedTags(taskType);
    if (tags.length > 0) dispatch(baseAPI.util.invalidateTags(tags));
  }, [completed, taskType, dispatch]);

  return (
    <ListItemButton selected={selected} onClick={onSelect} sx={{ pr: 0.5 }}>
      <ListItemText
        primary={taskType ?? 'Loading'}
        primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
        secondary={
          <Stack direction='row' alignItems='center' gap={1}>
            <StateChip state={state} size='small' />
            {task && (
              <Typography variant='caption' noWrap>
                {subjectOf(task.input as MeterTaskInput)}
              </Typography>
            )}
          </Stack>
        }
        secondaryTypographyProps={{ component: 'div', sx: { mt: 0.5 } }}
      />
      <IconButton
        size='small'
        color='inherit'
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <CloseIcon fontSize='small' />
      </IconButton>
    </ListItemButton>
  );
};

/** A watched task's history, its stored logs followed by its events as they arrive, and its details. */
const TaskPane = ({
  watched,
  watch,
}: {
  watched: WatchedTask;
  watch: TaskWatch;
}) => {
  const { events, task, state, streaming } = useWatchedTask(
    watch,
    watched.taskId
  );
  const finished =
    state.status !== undefined && finishedTaskStatuses.includes(state.status);
  const broken = events.some((e) => e.type === 'error');

  if (!streaming && events.length === 0) {
    return (
      <Typography color='error'>Could not attach to this task.</Typography>
    );
  }

  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        {(task?.logs ?? []).map((log, i) => (
          <Line key={`log-${i}`} at={log.timestamp} label={log.message} />
        ))}
        {historyOf(events).map(({ key, at, label, detail }) =>
          detail ? (
            <JsonSection
              key={key}
              title={<Line at={at} label={label} />}
              src={detail}
            />
          ) : (
            <Box key={key}>
              <Line at={at} label={label} />
            </Box>
          )
        )}
        {broken && (
          <Typography variant='body2' color='error'>
            The stream broke.
          </Typography>
        )}
        <Stack direction='row' alignItems='center' gap={1} sx={{ mt: 1 }}>
          {!finished && streaming && <CircularProgress size={12} />}
          <Typography variant='body2' color='text.secondary'>
            {finished
              ? 'Finished'
              : streaming
                ? 'Listening for events'
                : 'Stream ended'}
          </Typography>
        </Stack>
      </Stack>

      {task && (
        <JsonSection
          title={
            <Typography variant='body2' color='text.secondary'>
              Details
            </Typography>
          }
          src={detailsOf(task)}
        />
      )}
    </Stack>
  );
};

interface Props {
  tasks: WatchedTask[];
  selected: string;
  onSelect: (taskId: string) => void;
  onRemove: (taskId: string) => void;
  onClose: () => void;
}

/**
 * The tasks being watched down the side, each with its live status, and the
 * picked one's logs and events streamed beside them. One stream per scope
 * carries the events of all its tasks.
 */
const TaskWatchDialog = ({
  tasks,
  selected,
  onSelect,
  onRemove,
  onClose,
}: Props) => {
  const current = tasks.find((t) => t.taskId === selected) ?? tasks[0];
  const watches = useMemo(() => watchesOf(tasks), [tasks]);
  const watchOf = (t: WatchedTask) => watches.get(scopeKey(t.scope))!;

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {tasks.length === 1 ? 'Task' : `${tasks.length} tasks`}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', p: 0 }}>
        <List
          dense
          sx={{
            width: 240,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            overflowY: 'auto',
          }}
        >
          {tasks.map((t) => (
            <TaskRow
              key={t.taskId}
              watched={t}
              watch={watchOf(t)}
              selected={t.taskId === current.taskId}
              onSelect={() => onSelect(t.taskId)}
              onRemove={() => onRemove(t.taskId)}
            />
          ))}
        </List>
        <Box sx={{ flex: 1, minWidth: 0, p: 2, overflowY: 'auto' }}>
          <TaskPane
            key={current.taskId}
            watched={current}
            watch={watchOf(current)}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TaskWatchDialog;
